import { EMPTY_FIELD } from 'constants/emptyField';
import { Resources } from 'constants/resources';

import { usePermissions } from 'hooks/usePermissions';
import { MerchantPlatformField } from 'pages/Merchants/components/MerchantPlatformField/MerchantPlatformField';
import { MerchantReferralsField } from 'pages/Merchants/components/MerchantReferralsField/MerchantReferralsField';
import { MerchantsPartnersField } from 'pages/Merchants/components/MerchantsPartnersField/MerchantsPartnersField';
import { NotificationKeyTooltip } from 'pages/Merchants/components/NotificationKeyTooltip/NotificationKeyTooltip';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { Box } from 'shared/mui/Box';
import { CardTable } from 'shared/mui/Card/CardTableNew/CardTable';
import { Link } from 'shared/mui/Link/Link';
import { TableRowWrapper } from 'shared/mui/Table/TableRowWrapperNew/TableRowWrapper';
import { ChipField, DateTimeField, TextField } from 'shared/react-admin/Fields';
import { EmbeddedArrayField } from 'shared/react-admin/Fields/Embedded/EmbeddedArrayField/EmbeddedArrayField';
import { Company } from 'types/embedded/company';
import { Merchant } from 'types/embedded/merchant';

type MerchantsShowCommonInformationTableProps = {
  handleOpenCompanyModal: (company: Partial<Company>) => void;
  record?: Merchant;
};

export const MerchantsShowCommonInformationTable: React.FC<
  MerchantsShowCommonInformationTableProps
> = ({ handleOpenCompanyModal, record }) => {
  const translate = useTypedTranslate();

  const { hasResourceAccess } = usePermissions();

  return (
    <CardTable tableBodyProps={{ sx: { '& td': { width: '294px' } } }}>
      <TableRowWrapper label={translate('app.merchants.alias')}>
        <TextField source="alias" />
      </TableRowWrapper>
      {hasResourceAccess(Resources.Companies) && (
        <TableRowWrapper label={translate('app.merchants.company')}>
          <EmbeddedArrayField
            render={(values: Company[]) => {
              return (
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {values.map((company: Partial<Company>) => (
                    <ChipField
                      color="primary"
                      key={company.id || company?.['@id']}
                      onClick={() => handleOpenCompanyModal(company)}
                      record={company}
                      source="name"
                      sx={{
                        '.MuiChip-label': {
                          cursor: 'pointer',
                          lineHeight: '12px',
                        },
                      }}
                    />
                  ))}
                </Box>
              );
            }}
            sourceField="companies"
          />
          <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}></Box>
        </TableRowWrapper>
      )}
      <TableRowWrapper label={translate('app.merchants.referrals')}>
        <MerchantReferralsField />
      </TableRowWrapper>
      <TableRowWrapper label={translate('app.merchants.partner')}>
        <MerchantsPartnersField />
      </TableRowWrapper>
      <TableRowWrapper label={translate('app.merchants.platform')}>
        <MerchantPlatformField />
      </TableRowWrapper>
      <TableRowWrapper label={translate('app.merchants.notificationKey')}>
        {record?.notificationKey ? (
          <NotificationKeyTooltip
            render={(handleTooltipOpen) => (
              <Link onClick={handleTooltipOpen} sx={{ cursor: 'pointer' }}>
                {translate('app.buttons.showValue')}
              </Link>
            )}
            value={record.notificationKey}
          />
        ) : (
          translate('app.merchantAccounts.noAccess')
        )}
      </TableRowWrapper>
      <TableRowWrapper
        label={`${translate('app.merchants.channelIdFor')} ${translate('app.merchants.notificationsMismatch')}`}
      >
        <TextField emptyText={EMPTY_FIELD} source="missmatchChatId" />
      </TableRowWrapper>
      <TableRowWrapper label={translate('app.merchants.notificationLanguage')}>
        <TextField source="language.description" />
      </TableRowWrapper>
      <TableRowWrapper label={translate('app.merchants.createdAt')}>
        <DateTimeField inline source="createdAt" />
      </TableRowWrapper>
      <TableRowWrapper label={translate('app.merchants.updatedAt')}>
        <DateTimeField inline source="updatedAt" />
      </TableRowWrapper>
    </CardTable>
  );
};
